import React, { useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import editorStore from "../../stores/EditorStore";

const C = {
  workspace:    '#12141c',
  surface:      '#13151f',
  surface2:     '#1c1e2e',
  surface3:     '#252840',
  border:       'rgba(255,255,255,0.07)',
  text1:        '#f0f2ff',
  text2:        '#9da3c8',
  text3:        '#5c6290',
  accent:       '#5b79ff',
  accentSoft:   'rgba(91,121,255,0.15)',
  accentBorder: 'rgba(91,121,255,0.4)',
  accentGlow:   'rgba(91,121,255,0.35)',
};

interface Props {
  toggleWorkspaceEditor: boolean;
  onToggleWorkspace: (value: boolean) => void;
}

const PhotobookWorkspace = observer(({ toggleWorkspaceEditor, onToggleWorkspace }: Props) => {
  const canvasRef    = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const stripRef     = useRef<HTMLDivElement>(null);

  // Always init on mount — canvas div is always in the DOM (visibility toggled, not display:none)
  // so containerRef has real layout dimensions even when "hidden"
  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;
    editorStore.initCanvas(canvasRef.current, containerRef.current);

    // Keep the Fabric canvas background transparent on every render.
    // Each page switch calls loadFromJSON which restores backgroundColor:'#e5e7eb' from saved JSON.
    // This after:render guard detects the reset and re-applies transparency immediately.
    // The white page itself is a fabric.Rect object and is unaffected.
    const fc = editorStore.getCanvas();
    if (fc) {
      (fc as any).backgroundColor = '';
      fc.requestRenderAll();
      fc.on('after:render', () => {
        if ((fc as any).backgroundColor !== '') {
          (fc as any).backgroundColor = '';
          fc.requestRenderAll();
        }
      });
    }


    const ro = new ResizeObserver(() => editorStore.fitCanvasToContainer());
    ro.observe(containerRef.current);
    return () => { ro.disconnect(); editorStore.destroy(); };
  }, []);

  // Re-fit when editor becomes visible
  useEffect(() => {
    if (toggleWorkspaceEditor) {
      requestAnimationFrame(() => editorStore.fitCanvasToContainer());
    }
  }, [toggleWorkspaceEditor]);

  useEffect(() => {
    // Attach to containerRef, not canvasRef: Fabric.js creates an upperCanvas on top of the lower
    // canvas that captures all pointer/wheel events — the lower canvas (canvasRef) never receives them.
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!toggleWorkspaceEditor) return;
      // Only zoom while the cursor is over the page, not the surrounding workspace
      const rect = el.getBoundingClientRect();
      if (!editorStore.isScreenPointOnPage(e.clientX - rect.left, e.clientY - rect.top)) return;
      e.preventDefault();
      const active = editorStore.getCanvas()?.getActiveObject();
      if (active && (active as any).customType === 'frameImage') {
        editorStore.zoomFrameImage(active, e.deltaY);
      } else {
        editorStore.zoomAtPoint(e.offsetX, e.offsetY, e.deltaY);
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [toggleWorkspaceEditor]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!toggleWorkspaceEditor) return;
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const active = editorStore.getCanvas()?.getActiveObject();
      if (active && (active as any).isEditing) return;
      if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); editorStore.deleteSelected(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); e.shiftKey ? editorStore.history.redo() : editorStore.history.undo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') { e.preventDefault(); editorStore.history.redo(); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggleWorkspaceEditor]);

  // Scroll active thumbnail into view in the strip
  useEffect(() => {
    if (!stripRef.current) return;
    const el = stripRef.current.querySelector<HTMLElement>('[data-active="true"]');
    el?.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
  }, [editorStore.activePageId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleThumbnailClick = (pageId: string) => {
    editorStore.clearLastSelectedFrame();
    editorStore.selectPage(pageId);
    onToggleWorkspace(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const fc = editorStore.getCanvas();
    if (!fc) return;

    // Use Fabric's own getPointer — handles viewport transform, zoom, and pan correctly.
    const ptr    = fc.getPointer(e.nativeEvent as MouseEvent);
    const canvasX = ptr.x;
    const canvasY = ptr.y;

    const templateData = e.dataTransfer.getData('application/x-photobook-template');
    if (templateData) {
      try { editorStore.addPhotoFrames(JSON.parse(templateData).frames); } catch (_) {}
      return;
    }

    const imageData = e.dataTransfer.getData('application/x-image-item');
    if (!imageData) return;
    const imageItem = JSON.parse(imageData) as { id: number; src: string };

    // Fallback chain: point hit → last selected frame → first empty frame → free element
    const frame =
      editorStore.getFrameAtPoint(canvasX, canvasY) ??
      editorStore.getActiveFrame() ??
      editorStore.getLastSelectedFrame() ??
      editorStore.getFirstEmptyPhotoFrame();

    if (frame) { editorStore.placeImageInFrame(imageItem.src, frame); return; }

    const img = new Image();
    img.src = imageItem.src;
    img.onload = () => {
      const scale  = Math.min(editorStore.width / img.width, editorStore.height / img.height);
      const finalW = img.width  * scale;
      const finalH = img.height * scale;
      editorStore.activePage.addElement({
        type: 'image', src: imageItem.src,
        width: finalW, height: finalH,
        x: canvasX - finalW / 2, y: canvasY - finalH / 2,
      });
    };
  };

  const zoomPct       = Math.round(editorStore.scale * 100);
  const activeIdx     = editorStore.activePageIndex;
  const totalPages    = editorStore.pageIds.length;
  const isFirstPage   = activeIdx === 0;
  const isLastPage    = activeIdx === totalPages - 1;
  const DOT_GRID      = 'radial-gradient(circle, rgba(255,255,255,0.035) 1px, transparent 1px)';
  const THUMB_W       = 72;
  const THUMB_H       = 92;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>

      {/* ── Overview grid (shown on top when NOT in editor mode) ─────────────── */}
      {!toggleWorkspaceEditor && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 2, overflowY: 'auto',
          background: C.workspace,
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          padding: '24px',
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
            {editorStore.pageIds.map((pageId, index) => {
              const thumb    = editorStore.thumbnails[pageId];
              const isActive = editorStore.activePageId === pageId;
              return (
                <div
                  key={pageId}
                  onClick={() => handleThumbnailClick(pageId)}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                >
                  <div style={{
                    width: 120, height: 168, borderRadius: 6, overflow: 'hidden',
                    border: `2px solid ${isActive ? C.accent : 'rgba(255,255,255,0.1)'}`,
                    boxShadow: isActive
                      ? `0 0 0 3px ${C.accentGlow}, 0 4px 16px rgba(0,0,0,0.5)`
                      : '0 4px 16px rgba(0,0,0,0.4)',
                    background: '#fff',
                    transition: 'all 150ms',
                  }}
                    onMouseEnter={e => { if (!isActive) { e.currentTarget.style.borderColor = C.accentBorder; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
                    onMouseLeave={e => { if (!isActive) { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.transform = 'none'; } }}
                  >
                    {thumb
                      ? <img src={thumb} alt={`Page ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: 11 }}>Empty</div>
                    }
                  </div>
                  <span style={{ fontSize: 11, color: isActive ? C.accent : C.text3, fontWeight: isActive ? 600 : 400 }}>
                    Page {index + 1}
                  </span>
                </div>
              );
            })}

            {/* Add page tile */}
            <div
              onClick={() => { editorStore.addPage(); onToggleWorkspace(true); }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer' }}
            >
              <div style={{
                width: 120, height: 168, borderRadius: 6,
                border: `2px dashed ${C.border}`, background: C.surface2,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'all 150ms',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.accentBorder; e.currentTarget.style.background = C.accentSoft; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.surface2; }}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={C.text3} strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
                <span style={{ fontSize: 10, color: C.text3 }}>Add page</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Editor + canvas — ALWAYS MOUNTED
          position:absolute + visibility:hidden (not display:none!) so the container
          has real layout dimensions even when hidden — initCanvas measures them. ── */}
      <div style={{
        position: 'absolute', inset: 0,
        visibility: toggleWorkspaceEditor ? 'visible' : 'hidden',
        display: 'flex', flexDirection: 'column',
      }}>

        {/* Canvas workspace */}
        <div
          ref={containerRef}
          style={{
            flex: 1, position: 'relative', overflow: 'hidden',
            background: C.workspace,
            backgroundImage: DOT_GRID,
            backgroundSize: '24px 24px',
          }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {/* Hint badge — top right */}
          <div style={{
            position: 'absolute', top: 10, right: 14, zIndex: 10, pointerEvents: 'none',
            background: C.surface3, border: `1px solid ${C.border}`,
            padding: '6px 10px', borderRadius: 8, fontSize: 11, color: C.text3,
            display: 'flex', alignItems: 'center', gap: 6,
            boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            Click frame → drag to reposition ·&nbsp;
            <span style={{ color: C.accent, fontWeight: 600 }}>scroll</span>&nbsp;to zoom ·&nbsp;
            <span style={{ color: C.accent, fontWeight: 600 }}>Alt+drag</span>&nbsp;to pan
          </div>

          {/* Fabric canvas host */}
          <div style={{ position: 'absolute', inset: 0 }}>
            <canvas ref={canvasRef} />
          </div>

          {/* ── Page nav — centered floating pill (matches mockup) ── */}
          <div style={{
            position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
            zIndex: 10, display: 'flex', alignItems: 'center', gap: 8,
            background: C.surface2, border: `1px solid ${C.border}`,
            borderRadius: 12, padding: '4px 8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
            userSelect: 'none',
          }}>
            <button
              disabled={isFirstPage}
              onClick={() => {
                const prev = editorStore.pageIds[activeIdx - 1];
                if (prev) editorStore.selectPage(prev);
              }}
              style={{
                width: 28, height: 28, borderRadius: 6, border: 'none', background: 'none',
                color: isFirstPage ? C.text3 : C.text2, cursor: isFirstPage ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, opacity: isFirstPage ? 0.3 : 1, transition: 'all 150ms',
              }}
              onMouseEnter={e => { if (!isFirstPage) e.currentTarget.style.background = C.surface3; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
            >‹</button>

            <div style={{ fontSize: 11, fontWeight: 500, color: C.text2, padding: '0 8px', whiteSpace: 'nowrap' }}>
              Page&nbsp;<span style={{ color: C.text1, fontWeight: 700 }}>{activeIdx + 1}</span>&nbsp;of&nbsp;{totalPages}
            </div>

            <button
              disabled={isLastPage}
              onClick={() => {
                const next = editorStore.pageIds[activeIdx + 1];
                if (next) editorStore.selectPage(next);
              }}
              style={{
                width: 28, height: 28, borderRadius: 6, border: 'none', background: 'none',
                color: isLastPage ? C.text3 : C.text2, cursor: isLastPage ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, opacity: isLastPage ? 0.3 : 1, transition: 'all 150ms',
              }}
              onMouseEnter={e => { if (!isLastPage) e.currentTarget.style.background = C.surface3; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
            >›</button>
          </div>

          {/* ── Zoom controls — bottom right ── */}
          <div style={{
            position: 'absolute', bottom: 20, right: 20, zIndex: 10,
            display: 'flex', alignItems: 'center',
            background: C.surface2, border: `1px solid ${C.border}`,
            borderRadius: 12, overflow: 'hidden',
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
            userSelect: 'none',
          }}>
            <ZBtn onClick={() => editorStore.zoom(1 / 1.25)} title="Zoom out">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/>
              </svg>
            </ZBtn>
            <div style={{ width: 1, height: 20, background: C.border }} />
            <button
              onClick={() => editorStore.fitCanvasToContainer()}
              style={{
                padding: '0 12px', height: 36, background: 'none', border: 'none',
                fontSize: 11, fontWeight: 600, color: C.text1, cursor: 'pointer',
                minWidth: 52, textAlign: 'center',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = C.surface3)}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
              title="Fit to screen"
            >{zoomPct}%</button>
            <div style={{ width: 1, height: 20, background: C.border }} />
            <ZBtn onClick={() => editorStore.zoom(1.25)} title="Zoom in">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                <line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/>
              </svg>
            </ZBtn>
            <div style={{ width: 1, height: 20, background: C.border }} />
            <ZBtn onClick={() => editorStore.fitCanvasToContainer()} title="Fit canvas">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
              </svg>
            </ZBtn>
          </div>
        </div>

        {/* ── Thumbnail strip — always visible in editor mode ─────────────────── */}
        <div style={{
          height: 130, flexShrink: 0,
          background: C.surface, borderTop: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center',
        }}>
          <div
            ref={stripRef}
            style={{
              flex: 1, height: '100%',
              overflowX: 'auto', overflowY: 'hidden',
              display: 'flex', alignItems: 'center',
              gap: 10, padding: '12px 16px',
              scrollbarWidth: 'thin',
              scrollbarColor: `${C.surface3} transparent`,
            }}
          >
            {editorStore.pageIds.map((pageId, index) => {
              const thumb    = editorStore.thumbnails[pageId];
              const isActive = editorStore.activePageId === pageId;
              return (
                <div
                  key={pageId}
                  data-active={isActive ? 'true' : 'false'}
                  onClick={() => editorStore.selectPage(pageId)}
                  style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                >
                  <div style={{
                    width: THUMB_W, height: THUMB_H,
                    borderRadius: 8, overflow: 'hidden',
                    background: '#fff',
                    border: `2px solid ${isActive ? C.accent : 'transparent'}`,
                    boxShadow: isActive
                      ? `0 0 0 3px ${C.accentGlow}, 0 4px 12px rgba(0,0,0,0.4)`
                      : '0 1px 3px rgba(0,0,0,0.4)',
                    transition: 'all 150ms',
                  }}
                    onMouseEnter={e => { if (!isActive) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.5)'; } }}
                    onMouseLeave={e => { if (!isActive) { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.4)'; } }}
                  >
                    {thumb
                      ? <img src={thumb} alt={`Page ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc', fontSize: 9 }}>Empty</div>
                    }
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 600, color: isActive ? C.accent : C.text3, letterSpacing: '0.2px' }}>
                    {index === 0 ? 'Cover' : index}
                  </span>
                </div>
              );
            })}

            {/* Add page */}
            <div
              onClick={() => editorStore.addPage()}
              style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer' }}
            >
              <div style={{
                width: THUMB_W, height: THUMB_H, borderRadius: 8,
                border: `1.5px dashed ${C.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22, color: C.text3,
                transition: 'all 150ms',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accent; e.currentTarget.style.background = C.accentSoft; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.text3; e.currentTarget.style.background = 'none'; }}
              >+</div>
              <span style={{ fontSize: 10, fontWeight: 600, color: C.text3, letterSpacing: '0.2px' }}>Add</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const ZBtn: React.FC<{ onClick: () => void; title: string; children: React.ReactNode }> = ({ onClick, title, children }) => (
  <button
    onClick={onClick}
    title={title}
    style={{ width: 36, height: 36, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9da3c8' }}
    onMouseEnter={e => (e.currentTarget.style.background = '#252840')}
    onMouseLeave={e => (e.currentTarget.style.background = 'none')}
  >
    {children}
  </button>
);

export default PhotobookWorkspace;
